import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';

import { ApplicationContext } from '../context/context';
import { endorsedHardSoftSkillArraySchema } from '../data-contract/skill';
import { userHardSoftSkillsOptionsSchema } from '../data-contract/user-hard-soft-skills';

import type { EndorsedHardSoftSkill, HardSoftSkillMutation, SkillMutationSource } from '../data-contract/skill';
import type { UserHardSoftSkillsOptions } from '../data-contract/user-hard-soft-skills';
import type { Pathname } from '../http-fetcher/http-fetcher';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

export const updateOnUserSkillsMutationKey = 'update-on-user-skills-change';
export const getUserHardSoftSkillsQueryKey = (userId: string): readonly [`/${string}`, string] => {
    const url = `/users/${userId}/hard-soft-skills` as const;

    return [url, 'GET'];
};

export const useUserHardSoftSkillsOptionsQuery = (userId: string): UseQueryResult<UserHardSoftSkillsOptions> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const pathname: Pathname = `/users/${userId}/hard-soft-skills/options`;

    return useQuery({
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(userHardSoftSkillsOptionsSchema, {
                pathname,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'GET', userHardSoftSkillsOptionsSchema],
    });
};

export const useUserHardSoftSkillsQuery = ({
    enabled,
    userId,
}: {
    readonly enabled: boolean;
    readonly userId: string;
}): UseQueryResult<ReadonlyArray<EndorsedHardSoftSkill>> => {
    const { applicationState, validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const [pathname, httpOperation] = getUserHardSoftSkillsQueryKey(userId);

    return useQuery({
        enabled,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(endorsedHardSoftSkillArraySchema, {
                pathname,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, httpOperation, applicationState.language.code],
    });
};

export type UserHardSoftSkillsMutation = UseMutationResult<void, Error, ReadonlyArray<HardSoftSkillMutation>>;

export const useUserHardSoftSkillsMutation = (
    userId: string,
    source: SkillMutationSource,
): UserHardSoftSkillsMutation => {
    const { cobrainerApiFetcher } = useContext(ApplicationContext);
    const pathname: Pathname = `/users/${userId}/hard-soft-skills`;

    const queryClient = useQueryClient();
    const invalidateCache = (): void => {
        queryClient.invalidateQueries({
            queryKey: getUserHardSoftSkillsQueryKey(userId),
        });
    };

    return useMutation({
        mutationFn: async (payload: ReadonlyArray<HardSoftSkillMutation>): Promise<void> => {
            const result = await cobrainerApiFetcher.postJson({
                pathname,
                payload,
                requestOptions: {
                    searchParams: { source },
                },
            });

            if (result.isOk) {
                return;
            }

            throw result.error;
        },
        mutationKey: ['POST', pathname],
        onSuccess: invalidateCache,
    });
};
